import { useEffect } from 'react';
import { useStore, unsaved, getState } from '../state/store';
import { handleKey } from '../input/keymap';
import { openSearch, saveDraft } from '../app/actions';
import { comboOf } from '../input/keymap';
import { TopBar } from './TopBar';
import { Ribbon, Palette } from './Ribbon';
import { ScoreView } from './ScoreView';
import { Transport, StatusBar } from './Transport';
import { Visualizers } from './Visualizers';
import { CommandSearch } from './CommandSearch';
import { DialogHost, SettingsPanel, Tour, Toasts } from './Panels';
import { Tooltips } from './Tooltip';

export function Editor() {
  const mode = useStore((s) => s.mode);
  const loading = useStore((s) => s.doc.status === 'loading');

  // Global keyboard routing (capture phase so the command search opens before anything else).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const combo = comboOf(e);
      if (combo === 'Mod+K') {
        e.preventDefault();
        if (getState().searchOpen) return;
        openSearch();
        return;
      }
      if (handleKey(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // Unsaved-changes guard and local draft autosave.
  useEffect(() => {
    const before = (e: BeforeUnloadEvent) => {
      if (unsaved() > 0) {
        saveDraft();
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', before);
    let t: ReturnType<typeof setTimeout>;
    const unsub = useStore.subscribe((s, p) => {
      if (s.ed.score !== p.ed.score && !s.doc.slug) {
        clearTimeout(t);
        t = setTimeout(saveDraft, 800);
      }
    });
    return () => {
      window.removeEventListener('beforeunload', before);
      unsub();
      clearTimeout(t);
    };
  }, []);

  return (
    <div className={`app mode-${mode}`}>
      <TopBar />
      {mode === 'edit' && <Ribbon />}
      <div className="workspace">
        {mode === 'edit' && <Palette />}
        <main className="paper" aria-busy={loading}>
          {loading ? <div className="loading">Loading score…</div> : <ScoreView />}
        </main>
      </div>
      <Visualizers />
      <Transport />
      <StatusBar />
      <SettingsPanel />
      <CommandSearch />
      <DialogHost />
      <Tour />
      <Toasts />
      <Tooltips />
    </div>
  );
}
